<script setup lang="ts">
import {
  DeleteOutlined,
  LinkOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { onMounted, ref } from 'vue'
import {
  createOtaConfig,
  deleteOtaConfig,
  fetchOtaConfigs,
  type OtaSyncConfig,
  syncOtaCalendar,
  updateOtaConfig,
} from '@/services/otaSync'

const props = defineProps<{ propertyId: string }>()

const configs = ref<OtaSyncConfig[]>([])
const loading = ref(false)
const syncing = ref<Record<string, boolean>>({})
const addProvider = ref('airbnb')
const addUrl = ref('')

const providers = [
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'booking', label: 'Booking.com' },
  { value: 'vrbo', label: 'VRBO' },
  { value: 'google', label: 'Google Calendar' },
  { value: 'other', label: 'Other' },
]

const load = async () => {
  loading.value = true
  try {
    configs.value = await fetchOtaConfigs(props.propertyId)
  } catch {
    configs.value = []
  } finally {
    loading.value = false
  }
}

const add = async () => {
  if (!addUrl.value.trim()) return
  try {
    await createOtaConfig(props.propertyId, addProvider.value, addUrl.value.trim())
    addUrl.value = ''
    message.success('OTA connection added. Click Sync to import now.')
    await load()
  } catch (e: any) {
    message.error(e?.message || 'Failed to add')
  }
}

const sync = async (config: OtaSyncConfig) => {
  syncing.value[config.id] = true
  try {
    const result = await syncOtaCalendar(config)
    message.success(`${result.imported} bookings synced from ${config.provider}`)
    await load()
  } catch (e: any) {
    message.error(e?.message || `Sync failed. Make sure the iCal URL is correct.`)
  } finally {
    syncing.value[config.id] = false
  }
}

const remove = async (config: OtaSyncConfig) => {
  try {
    await deleteOtaConfig(config.id)
    message.success(`${config.provider} connection removed`)
    await load()
  } catch (e: any) {
    message.error(e?.message || 'Failed to remove')
  }
}

const getProviderColor = (p: string) => {
  const colors: Record<string, string> = {
    airbnb: '#FF5A5F',
    booking: '#003580',
    vrbo: '#2E77AE',
    google: '#4285F4',
  }
  return colors[p] || '#666'
}

onMounted(load)
</script>

<template>
  <div>
    <a-card title="Setup Instructions" size="small" style="margin-bottom: 16px">
      <a-collapse :bordered="false" accordion>
        <a-collapse-panel key="airbnb" header="Airbnb">
          <ol style="margin: 0; padding-left: 20px; line-height: 2; font-size: 13px">
            <li>Go to <strong>Airbnb.com</strong> → <strong>Listings</strong> → select your property</li>
            <li>Click <strong>Calendar</strong> → <strong>Availability settings</strong></li>
            <li>Scroll to <strong>Sync calendars</strong> → click <strong>Export calendar</strong></li>
            <li>Copy the URL shown (starts with <code>https://www.airbnb.com/calendar/ical/</code>)</li>
            <li>Paste it in the <strong>Connect</strong> field below with <strong>Airbnb</strong> selected</li>
            <li>Click <strong>Connect</strong>, then <strong>Sync Now</strong></li>
          </ol>
          <a-alert type="info" show-icon style="margin-top: 8px; font-size: 12px">
            <template #message>
              <strong>Import only</strong> — Airbnb bookings appear in your CasaMana calendar but are managed on Airbnb.
              To block dates on Airbnb when CasaMana has bookings, paste your CasaMana export URL into Airbnb's <strong>Import calendar</strong> field.
            </template>
          </a-alert>
        </a-collapse-panel>

        <a-collapse-panel key="booking" header="Booking.com">
          <ol style="margin: 0; padding-left: 20px; line-height: 2; font-size: 13px">
            <li>Go to <strong>Booking.com</strong> → <strong>Extranet</strong> → <strong>Property</strong></li>
            <li>Click <strong>Calendar & Pricing</strong> → <strong>Sync calendars</strong></li>
            <li>Under <strong>Export calendar</strong>, copy the iCal URL</li>
            <li>Paste it in the <strong>Connect</strong> field below with <strong>Booking.com</strong> selected</li>
            <li>Click <strong>Connect</strong>, then <strong>Sync Now</strong></li>
          </ol>
          <a-alert type="info" show-icon style="margin-top: 8px; font-size: 12px">
            <template #message>
              <strong>Import + Export</strong> — Booking.com supports both import and export.
              Paste the export URL here, and paste CasaMana's export URL (coming soon) into Booking.com's Import field.
            </template>
          </a-alert>
        </a-collapse-panel>

        <a-collapse-panel key="vrbo" header="VRBO / HomeAway">
          <ol style="margin: 0; padding-left: 20px; line-height: 2; font-size: 13px">
            <li>Go to <strong>VRBO.com</strong> → <strong>Owner Dashboard</strong> → select your listing</li>
            <li>Click <strong>Calendar</strong> → <strong>Import/Export calendar</strong></li>
            <li>Under <strong>Export calendar</strong>, copy the iCal URL</li>
            <li>Paste it in the <strong>Connect</strong> field below with <strong>VRBO</strong> selected</li>
            <li>Click <strong>Connect</strong>, then <strong>Sync Now</strong></li>
          </ol>
        </a-collapse-panel>

        <a-collapse-panel key="google" header="Google Calendar">
          <ol style="margin: 0; padding-left: 20px; line-height: 2; font-size: 13px">
            <li>Open <strong>Google Calendar</strong> → left sidebar → hover your calendar → <strong>⋮ → Settings</strong></li>
            <li>Scroll to <strong>Integrate calendar</strong> → copy <strong>Secret address in iCal format</strong> (ends with <code>.ics</code>)</li>
            <li>Paste it below with <strong>Google Calendar</strong> selected and click <strong>Connect</strong> → <strong>Sync Now</strong></li>
            <li>Google events appear in CasaMana calendar as <a-tag color="#4285F4">google</a-tag> bookings (read-only)</li>
          </ol>
          <a-alert type="info" show-icon style="margin-top: 8px; font-size: 12px">
            <template #message>
              Uses iCal import (same as Airbnb). For two-way push (CasaMana → Google), add CasaMana's export URL (coming soon) as a Google Calendar subscription.
            </template>
          </a-alert>
        </a-collapse-panel>

        <a-collapse-panel key="other" header="Other Platform (iCal URL)">
          <ol style="margin: 0; padding-left: 20px; line-height: 2; font-size: 13px">
            <li>Find your platform's <strong>calendar export/sync</strong> settings</li>
            <li>Look for an <strong>iCal URL</strong> or <strong>Export calendar</strong> option</li>
            <li>Copy the URL (must end with <code>.ics</code>)</li>
            <li>Paste it in the <strong>Connect</strong> field below with <strong>Other</strong> selected</li>
            <li>Click <strong>Connect</strong>, then <strong>Sync Now</strong></li>
          </ol>
          <a-alert type="warning" show-icon style="margin-top: 8px; font-size: 12px">
            <template #message>
              Some platforms use different formats. If sync fails, verify your iCal URL works by opening it in a browser.
            </template>
          </a-alert>
        </a-collapse-panel>
      </a-collapse>
    </a-card>

    <a-alert
      type="info"
      show-icon
      style="margin-bottom: 16px; font-size: 13px"
      message="Paste your Airbnb or Booking.com iCal URL below to import bookings. Find it in your listing's calendar settings."
    />

    <a-space direction="vertical" style="width: 100%; margin-bottom: 16px">
      <a-row :gutter="8">
        <a-col :span="6">
          <a-select v-model:value="addProvider" :options="providers" style="width: 100%" />
        </a-col>
        <a-col :span="14">
          <a-input v-model:value="addUrl" placeholder="https://www.airbnb.com/calendar/ical/..." />
        </a-col>
        <a-col :span="4">
          <a-button type="primary" :disabled="!addUrl.trim()" @click="add" block>
            <LinkOutlined /> Connect
          </a-button>
        </a-col>
      </a-row>
    </a-space>

    <a-spin :spinning="loading">
      <a-list v-if="configs.length > 0" :data-source="configs" size="small" :split="true" bordered>
        <template #renderItem="{ item }">
          <a-list-item>
            <a-list-item-meta>
              <template #avatar>
                <a-tag :color="getProviderColor(item.provider)">{{ item.provider }}</a-tag>
              </template>
              <template #title>
                <a-space>
                  <span style="font-size: 13px">{{ item.import_url?.substring(0, 60) }}...</span>
                </a-space>
              </template>
              <template #description>
                <span v-if="item.last_synced_at" style="font-size: 12px; color: var(--text-secondary)">
                  Last synced: {{ new Date(item.last_synced_at).toLocaleString() }}
                </span>
                <span v-else style="font-size: 12px; color: var(--text-secondary)">Never synced</span>
              </template>
            </a-list-item-meta>
            <template #actions>
              <a-button size="small" :loading="syncing[item.id]" @click="sync(item)">
                <ReloadOutlined /> Sync Now
              </a-button>
              <a-popconfirm title="Remove this OTA connection?" @confirm="remove(item)">
                <a-button size="small" danger><DeleteOutlined /></a-button>
              </a-popconfirm>
            </template>
          </a-list-item>
        </template>
      </a-list>
      <div v-else style="text-align: center; padding: 24px; color: var(--text-secondary); font-size: 13px">
        No OTA connections yet. Add your first iCal URL above.
      </div>
    </a-spin>
  </div>
</template>
